/**
 * Guards against third-party DOM mutation crashing React.
 *
 * Browser translation (Google Translate, Safari/Edge auto-translate), password
 * managers and some extensions rewrite text nodes React owns. When React later
 * tries to remove or reorder those nodes it throws:
 *   NotFoundError: Failed to execute 'removeChild' on 'Node'
 * which is uncaught (it happens inside the commit phase) and blanks the app.
 *
 * The fix is to make removeChild/insertBefore no-op instead of throwing when the
 * node is no longer where React expects. React's virtual DOM re-syncs on the next
 * render, so this degrades gracefully rather than crashing.
 *
 * Applied once, only in the browser.
 */
let installed = false;

export const installDomGuard = () => {
  if (installed || typeof Node === "undefined") return;
  installed = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      if (import.meta.env.DEV) {
        console.warn("[domGuard] Skipped removeChild for a node moved by an external script.");
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (import.meta.env.DEV) {
        console.warn("[domGuard] Skipped insertBefore for a node moved by an external script.");
      }
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
